/**
 * Branding menu — "Explore CALS" dropdown anchored to the site logo.
 *
 * Mirrors the megamenu UX for a single trigger/panel pair:
 *  - Desktop: hover with open delay + close grace period
 *  - Mobile/touch: click/tap to toggle (hover disabled)
 *  - Native <button> gives Enter/Space → click for free
 *  - Escape closes and restores focus to trigger
 *  - Click outside closes
 *  - Tab past last focusable item closes naturally
 *  - Shift+Tab from first focusable item closes + restores focus to trigger
 *  - Respects prefers-reduced-motion (via CSS)
 */

const HOVER_OPEN_DELAY = 200;
const HOVER_CLOSE_DELAY = 300;

const isTouchDevice = () =>
  window.matchMedia('(pointer: coarse)').matches &&
  !window.matchMedia('(pointer: fine)').matches;

const openPanel = (trigger, panel) => {
  trigger.setAttribute('aria-expanded', 'true');
  panel.hidden = false;
  void panel.offsetHeight; // force reflow so the transition starts from hidden
  panel.classList.add('is-open');
};

const closePanel = (trigger, panel, restoreFocus = false) => {
  trigger.setAttribute('aria-expanded', 'false');
  panel.classList.remove('is-open');

  const onEnd = () => {
    panel.removeEventListener('transitionend', onEnd);
    panel.hidden = true;
  };
  panel.addEventListener('transitionend', onEnd);

  if (restoreFocus) trigger.focus();
};

const getFocusable = (el) =>
  [...el.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )];

const initBrandingMenu = () => {
  const trigger = document.querySelector('.branding-trigger');
  const panel = document.getElementById('branding-menu-panel');
  if (!trigger || !panel) return;

  const isOpen = () => trigger.getAttribute('aria-expanded') === 'true';

  // Trigger is a link to the homepage — let clicks navigate by default.
  // On touch devices (where hover can't open the menu), intercept the click
  // to toggle instead so the switcher stays reachable.
  trigger.addEventListener('click', (e) => {
    if (!isTouchDevice()) return;
    e.preventDefault();
    if (isOpen()) {
      closePanel(trigger, panel);
    } else {
      openPanel(trigger, panel);
    }
  });

  // Hover intent — desktop only.
  let openTimeout = null;
  let closeTimeout = null;
  const clearTimers = () => {
    clearTimeout(openTimeout);
    clearTimeout(closeTimeout);
  };

  const scheduleOpen = () => {
    if (isTouchDevice()) return;
    clearTimers();
    openTimeout = setTimeout(() => {
      if (!isOpen()) openPanel(trigger, panel);
    }, HOVER_OPEN_DELAY);
  };

  const scheduleClose = () => {
    if (isTouchDevice()) return;
    clearTimers();
    closeTimeout = setTimeout(() => {
      if (isOpen()) closePanel(trigger, panel);
    }, HOVER_CLOSE_DELAY);
  };

  trigger.addEventListener('mouseenter', scheduleOpen);
  trigger.addEventListener('mouseleave', scheduleClose);
  panel.addEventListener('mouseenter', () => {
    if (!isTouchDevice()) clearTimers();
  });
  panel.addEventListener('mouseleave', scheduleClose);

  // Escape closes and restores focus.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!isOpen()) return;
    e.preventDefault();
    closePanel(trigger, panel, true);
  });

  // Click outside closes without restoring focus.
  document.addEventListener('click', (e) => {
    if (!isOpen()) return;
    if (panel.contains(e.target) || trigger.contains(e.target)) return;
    closePanel(trigger, panel);
  });

  // Tab past last focusable item → close naturally.
  panel.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    const focusable = getFocusable(panel);
    if (focusable.length && document.activeElement === focusable[focusable.length - 1]) {
      closePanel(trigger, panel);
    }
  });

  // Shift+Tab from first focusable → close + restore focus.
  panel.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !e.shiftKey) return;
    const focusable = getFocusable(panel);
    if (focusable.length && document.activeElement === focusable[0]) {
      e.preventDefault();
      closePanel(trigger, panel, true);
    }
  });
};

document.addEventListener('DOMContentLoaded', initBrandingMenu);
